'use client';

import Link from 'next/link';
import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import EnrollmentForm from '@/components/EnrollmentForm';
import EnrollmentModal from '@/components/EnrollmentModal';

interface Pathway {
  id: number;
  title: string;
  description: string;
  icon: string;
  color: string;
  duration: string;
  programs: number;
  targetRole: string;
  skills: string[];
  salaryRange: string;
}

interface LearningPathwaysProps {
  className?: string;
}

const LearningPathways = ({ className = '' }: LearningPathwaysProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const pathways: Pathway[] = [
    {
      id: 1,
      title: 'Software Development',
      description: 'Master full-stack development with modern frameworks and build production-ready apps.',
      icon: 'CodeBracketIcon',
      color: 'primary',
      duration: '6-8 months',
      programs: 12,
      targetRole: 'Full Stack Developer',
      skills: ['Spring Boot', 'Spring AI', 'Angular', 'React', 'MERN', 'MEAN', '+2'],
      salaryRange: '',
    },
    {
      id: 2,
      title: 'Data Science & AI',
      description: 'Learn machine learning, data analysis, and AI to solve complex business problems',
      icon: 'ChartBarIcon',
      color: 'accent',
      duration: '8-10 months',
      programs: 10,
      targetRole: 'Data Scientist',
      skills: ['Python', 'PowerBI', 'Statistics', 'ML', 'Gen AI', '+6'],
      salaryRange: '',
    },
    {
      id: 3,
      title: 'Cloud & DevOps',
      description: 'Design scalable cloud infrastructure and automate deployment pipelines',
      icon: 'CloudIcon',
      color: 'action-blue',
      duration: '5-7 months',
      programs: 8,
      targetRole: 'Cloud Architect',
      skills: ['Linux', 'AWS', 'Docker', 'Kubernetes', 'CI/CD', '+7'],
      salaryRange: '',
    }
  ];

  return (
    <section className={`py-20 bg-background ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full">
            <Icon name="MapIcon" size={20} variant="solid" className="text-accent" />
            <span className="text-sm font-body font-semibold text-accent">Learning Pathways</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headline font-bold text-foreground">
            Choose Your Career Path
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            Structured learning journeys designed to take you from beginner to job-ready professional
          </p>
        </div>

        {/* Pathways Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {pathways.map((pathway) => (
            <div
              key={pathway.id}
              className="group bg-card rounded-2xl p-8 border border-border hover:border-primary/30 transition-all duration-300 hover:shadow-sky-md space-y-6"
            >
              {/* Icon & Title */}
              <div className="space-y-4">
                <div className={`w-16 h-16 bg-${pathway.color}/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                  <Icon name={pathway.icon as any} size={32} variant="solid" className={`text-${pathway.color}`} />
                </div>
                <div>
                  <h3 className="text-2xl font-headline font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                    {pathway.title}
                  </h3>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed">
                    {pathway.description}
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 py-4 border-y border-border">
                <div>
                  <p className="text-xs text-muted-foreground font-body mb-1">Duration</p>
                  <p className="text-sm font-body font-semibold text-foreground">{pathway.duration}</p>
                </div>
              </div>

              {/* Target Role */}
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-body uppercase tracking-wide">Target Role</p>
                <p className="text-base font-headline font-bold text-foreground">{pathway.targetRole}</p>
                <p className="text-sm text-success font-body font-semibold">{pathway.salaryRange}</p>
              </div>

              {/* Skills */}
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground font-body uppercase tracking-wide">Key Skills</p>
                <div className="flex flex-wrap gap-2">
                  {pathway.skills.map((skill, index) => (
                    <span
                      key={index}
                      className={`px-3 py-1 bg-${pathway.color}/10 text-${pathway.color} text-xs font-body font-medium rounded-full`}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <Link
                href="/programs-overview"
                className="flex items-center justify-center w-full px-6 py-3 bg-primary/10 text-primary rounded-lg font-body font-semibold text-sm hover:bg-primary hover:text-primary-foreground transition-all duration-300 group-hover:shadow-sky-sm"
              >
                Explore Pathway
                <Icon name="ArrowRightIcon" size={16} variant="outline" className="ml-2" />
              </Link>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-12 border border-primary/20">
          <div className="max-w-3xl mx-auto space-y-6">
            <h3 className="text-2xl sm:text-3xl font-headline font-bold text-foreground">
              Not Sure Which Path to Choose?
            </h3>
            <p className="text-base text-muted-foreground font-body">
              Take our free career assessment to discover the perfect learning pathway based on your interests, skills, and career goals
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/programs-overview"
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-lg font-headline font-semibold text-base shadow-sky-md hover:shadow-sky-lg transition-all duration-300 hover:scale-105"
              >
                Take Career Assessment
                <Icon name="ClipboardDocumentCheckIcon" size={20} variant="outline" className="ml-2" />
              </Link>
              <button
                onClick={() => setIsModalOpen(true)}
                className="inline-flex items-center justify-center px-8 py-4 bg-card text-foreground border-2 border-primary/20 rounded-lg font-headline font-semibold text-base hover:bg-primary/5 transition-all duration-300"
              >
                Talk to Advisor
                <Icon name="ChatBubbleLeftRightIcon" size={20} variant="outline" className="ml-2" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Enrollment Modal */}
      <EnrollmentModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <EnrollmentForm onClose={() => setIsModalOpen(false)} />
      </EnrollmentModal>
    </section>
  );
};

export default LearningPathways;